import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { t } from 'src/i18n';
import { BaseDialog } from 'src/library/components/BaseDialog';
import '../fields/d-view-info';
import '../editors/elements/d-checkbox';
import type { DialogActionInput } from 'src/library/components/DialogActionInput';
import type { AlertDialogInput } from 'src/library/components/alert-dialog-input';
import type { AlertDialogResult } from 'src/library/components/alert-dialog-result';

@customElement('alert-dialog')
export class AlertDialog extends BaseDialog<AlertDialogInput, AlertDialogResult> {
  static readonly styles = [
    ...BaseDialog.styles,
    css`
      d-checkbox {
        margin-top: 10px;
      }
      .footer-actions {
        border: none;
      }
    `,
  ];
  @property({ type: Number, attribute: 'width' })
  width = 400;
  @property({ type: String })
  title: string = '';
  @property({ type: String })
  content = '';
  @property({ type: Boolean })
  noOption = false;
  @property({ type: Boolean })
  dontShowAgainOption = false;
  @property({ type: Boolean })
  dontShowAgain = false;
  @property({ type: Boolean })
  okDisabled = false;

  protected get calculatedFooterActions(): DialogActionInput[] {
    if (this.noOption) {
      return [{ name: t('common:ok'), action: 'close', main: true }];
    }
    return [
      { name: t('common:cancel'), action: 'close' },
      { name: t('common:ok'), action: 'ok', main: true, disabled: this.okDisabled },
    ];
  }

  protected fetchResult(detail: string | undefined): AlertDialogResult {
    if (detail === 'ok') {
      return {
        action: 'ok',
        dontShowAgain: this.dontShowAgain ?? false,
      };
    }
    return {
      action: 'cancel',
    };
  }

  protected initializeDialog(input: AlertDialogInput) {
    this.title = input.title;
    this.content = input.content;
    this.noOption = input.noOption ?? false;
    this.dontShowAgainOption = input.dontShowAgainOption ?? false;
    this.okDisabled = input.okDisabled ?? false;
  }

  private dontShowAgainChanged(e: CustomEvent<{ checked: boolean }>) {
    this.dontShowAgain = e.detail.checked;
  }

  protected renderBody() {
    return html`
      <d-view-info .content=${this.content} class="fullWidth"></d-view-info>
      ${this.dontShowAgainOption
        ? html`
            <d-checkbox
              @checked-changed=${(e: CustomEvent<{ checked: boolean }>) => this.dontShowAgainChanged(e)}
              ?checked=${this.dontShowAgain}
              .optionLabel=${t('alertDialog.dontShowAgain')}
              theme-page
            ></d-checkbox>
          `
        : nothing}
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'alert-dialog': AlertDialog;
  }
}
